import { useContext, useEffect, useRef, useState } from 'react'
import { useQuery, useQueryClient } from 'react-query'
import { Portal, Typography } from '@mui/material'

import AuthContext from 'context/AuthContext'
import {
  ANALYTICS_EVENT,
  ANIMATION_LEVELS,
  CLAIM_ALL_SOURCE,
  ONBOARDING_STEP,
} from 'constants/index'
import { claimAllRewards, getAllUnclaimedRewards } from 'services/userRewardsService'
import { getAnimationLevel, handleHeaderCounter } from 'utils/animations'
import {
  createVirtualCurrencyImagesArrays,
  formatNumber,
  handleError,
  sendGA4Event,
} from 'utils/utils'

/** The query Quests reads too, so both pages share one copy of the unclaimed rewards. */
const UNCLAIMED_REWARDS_QUERY = 'allUnclaimedRewards'

/** How long "Added to your balance" stays before the banner closes. */
const ADDED_MESSAGE_MS = 2500

const NO_REWARDS = { rewards: [], aggregatedCoins: 0, aggregatedPiggyBanks: 0 }

const Pig = ({ size = 17 }) => (
  <img src="/images/pig.png" alt="piggy banks" width={size} height={size} />
)
const Coin = ({ size = 17 }) => (
  <img src="/images/coin.png" alt="coins" width={size} height={size} />
)

/**
 * The top banner for rewards the user earned but has not claimed yet. "Claim All" flies the
 * currencies to the header, then the banner reads "Added to your balance" for a moment.
 */
const ClaimBanner = ({ experiment }) => {
  const { user, updateUserData } = useContext(AuthContext)
  const queryClient = useQueryClient()

  const isOnboardingComplete =
    !user?.onboardingStep || user.onboardingStep === ONBOARDING_STEP.COMPLETE

  const { data } = useQuery(UNCLAIMED_REWARDS_QUERY, getAllUnclaimedRewards, {
    onError: handleError,
    enabled: isOnboardingComplete,
  })

  const [claimed, setClaimed] = useState(null)
  const [flyingCoins, setFlyingCoins] = useState([])
  const [flyingPigs, setFlyingPigs] = useState([])

  // The user the claim returns is applied once the header counters finish animating,
  // otherwise the header would jump to the new balance under them.
  const pendingUser = useRef(null)
  const isShowingAdded = useRef(false)

  useEffect(() => {
    if (!claimed) {
      return
    }

    isShowingAdded.current = true
    const timeout = setTimeout(() => {
      setClaimed(null)
      setFlyingCoins([])
      setFlyingPigs([])
    }, ADDED_MESSAGE_MS)

    // Runs when the message closes and also when the user leaves the page first, so the new
    // balance is always applied; a claim that answers later updates the user directly.
    return () => {
      clearTimeout(timeout)
      isShowingAdded.current = false
      if (pendingUser.current) {
        updateUserData(pendingUser.current)
        pendingUser.current = null
      }
    }
  }, [claimed])

  const piggyBanks = data?.aggregatedPiggyBanks || 0
  const coins = data?.aggregatedCoins || 0

  const handleClaim = (event) => {
    const animationLevel = getAnimationLevel(piggyBanks)

    claimAllRewards()
      .then((response) => {
        const updatedUser = response.data.updatedUser
        if (isShowingAdded.current) {
          pendingUser.current = updatedUser
        } else {
          updateUserData(updatedUser)
        }
      })
      .catch((error) => {
        handleError(error)
        // Nothing was added: drop the message so the rewards show up to claim again.
        setClaimed(null)
        setFlyingCoins([])
        setFlyingPigs([])
        queryClient.invalidateQueries(UNCLAIMED_REWARDS_QUERY)
      })

    createVirtualCurrencyImagesArrays(
      event.currentTarget,
      coins,
      piggyBanks,
      setFlyingCoins,
      setFlyingPigs,
      0.7,
      animationLevel
    )
    const displayVFXBehindCurrencyIcon = animationLevel >= ANIMATION_LEVELS.LEVEL_2
    setTimeout(() => {
      handleHeaderCounter(false, coins, displayVFXBehindCurrencyIcon)
      handleHeaderCounter(true, piggyBanks, displayVFXBehindCurrencyIcon)
    }, 700)

    sendGA4Event(ANALYTICS_EVENT.CLAIM_ALL_REWARDS, {
      source: CLAIM_ALL_SOURCE.HOME_BANNER,
      piggy_banks: piggyBanks,
      coins,
      rewards: data?.rewards?.length ?? 0,
      ...experiment,
    })

    setClaimed({ piggyBanks, coins })
    queryClient.setQueryData(UNCLAIMED_REWARDS_QUERY, NO_REWARDS)
  }

  // On the body, so they start from the button on the page rather than inside the sticky
  // banners, and always in the same place, so closing the banner never restarts their flight.
  // body2 is what sizes them, as on Quests.
  const flyingCurrencies = (
    <Portal>
      <Typography variant="body2">{flyingCoins}</Typography>
      <Typography variant="body2">{flyingPigs}</Typography>
    </Portal>
  )

  if (claimed) {
    return (
      <>
        {flyingCurrencies}
        <div className="gamesRedesignTopBanner added" role="status">
          {claimed.piggyBanks > 0 && (
            <span className="gamesRedesignTopBannerAmount">
              + {formatNumber(claimed.piggyBanks)}
              <Pig />
            </span>
          )}
          {claimed.coins > 0 && (
            <span className="gamesRedesignTopBannerAmount">
              + {formatNumber(claimed.coins)}
              <Coin />
            </span>
          )}
          <span className="gamesRedesignTopBannerText">Added to your balance</span>
        </div>
      </>
    )
  }

  if (!isOnboardingComplete || !data?.rewards?.length) {
    return <>{flyingCurrencies}</>
  }

  return (
    <>
      {flyingCurrencies}
      <div className="gamesRedesignTopBanner">
        <span className="gamesRedesignTopBannerRewards">
          {piggyBanks > 0 && (
            <span className="gamesRedesignRewardPill pig">
              <span className="gamesRedesignRewardPillValue">{formatNumber(piggyBanks)}</span>
              <Pig size={18} />
            </span>
          )}
          {coins > 0 && (
            <span className="gamesRedesignRewardPill coin">
              <span className="gamesRedesignRewardPillValue">{formatNumber(coins)}</span>
              <Coin size={18} />
            </span>
          )}
        </span>
        <span className="gamesRedesignTopBannerText">ready to claim!</span>
        <button type="button" className="gamesRedesignClaimAll" onClick={handleClaim}>
          <span className="gamesRedesignClaimAllLabel">Claim All</span>
        </button>
      </div>
    </>
  )
}

export default ClaimBanner
